import { useState, useEffect, useRef } from 'react';

export default function Home() {
  const [showConsultForm, setShowConsultForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.scroll-reveal');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/public/form/submit/oldgym-consult', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', phone: '', message: '' });
        setTimeout(() => {
          setShowConsultForm(false);
          setSubmitSuccess(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* HERO Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/44ad2e8efa092dafc98e4f954e0bc538/67056a476eef48a8a21cabe93c0abe2b.png"
            alt="올드짐 수월점"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* Top Scroll Indicator */}
        <div className="absolute top-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-10 h-10 flex items-center justify-center animate-scroll-bounce">
            <i className="ri-arrow-down-s-line text-3xl text-white/70"></i>
          </div>
        </div>
        
        <div className="relative z-10 px-6 text-center">
          <img 
            src="https://static.readdy.ai/image/44ad2e8efa092dafc98e4f954e0bc538/cf9314c8b5e6db1b9a290e0b241586ae.png"
            alt="올드짐 수월점"
            className="w-24 h-24 mx-auto mb-8 object-contain scroll-reveal opacity-0"
            style={{ animationDelay: '0.2s' }}
          />
          <h1 className="text-3xl font-bold text-white leading-tight mb-6 scroll-reveal opacity-0" style={{ fontFamily: "'Noto Sans KR', sans-serif", animationDelay: '0.5s' }}>
            여성분들이 먼저<br />알아보고 선택한 헬스장
          </h1>
          <p className="text-xl text-white/95 mb-8 font-medium scroll-reveal opacity-0" style={{ animationDelay: '0.8s' }}>
            깨끗한 시설, 24시 언제든 편하게
          </p>
          <p className="text-base text-white/85 leading-relaxed px-4 scroll-reveal opacity-0" style={{ animationDelay: '1.1s' }}>
            처음이어도 괜찮습니다.<br />
            혼자서도 운동할 수 있을 때까지 함께합니다.
          </p>
        </div>

        {/* Bottom Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-10 h-10 flex items-center justify-center animate-scroll-bounce">
            <i className="ri-arrow-down-s-line text-3xl text-white/70"></i>
          </div>
        </div>
      </section>

      {/* 공감 카드 섹션 */}
      <section className="py-16 px-6">
        <h2 className="text-2xl font-bold text-center mb-12 text-[#4A4A4A] scroll-reveal opacity-0" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
          이런 이유로 헬스장을<br />그만두셨나요?
        </h2>
        
        <div className="space-y-5 max-w-md mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E8E4DF] scroll-reveal opacity-0" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-eye-off-line text-3xl text-[#A0826D]"></i>
              </div>
              <p className="text-lg text-[#4A4A4A] leading-relaxed pt-2">
                눈치 보며 운동해야 했던 분위기
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E8E4DF] scroll-reveal opacity-0" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-3xl text-[#A0826D]"></i>
              </div>
              <p className="text-lg text-[#4A4A4A] leading-relaxed pt-2">
                등록 후 이어지는 PT 권유
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E8E4DF] scroll-reveal opacity-0" style={{ animationDelay: '0.6s' }}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-3xl text-[#A0826D]"></i>
              </div>
              <p className="text-lg text-[#4A4A4A] leading-relaxed pt-2">
                청결이 신경 쓰였던 환경
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E8E4DF] scroll-reveal opacity-0" style={{ animationDelay: '0.8s' }}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-3xl text-[#A0826D]"></i>
              </div>
              <p className="text-lg text-[#4A4A4A] leading-relaxed pt-2">
                운동 시간이 맞지 않아서
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 전환 문장 */}
      <section className="py-20 px-6">
        <div className="max-w-md mx-auto text-center">
          <p className="text-2xl font-bold text-[#4A4A4A] leading-relaxed scroll-reveal opacity-0" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
            그래서 올드짐은<br />다르게 운영합니다
          </p>
        </div>
      </section>

      {/* 운영 철학 카드 */}
      <section className="py-16 px-6 bg-white">
        <div className="space-y-5 max-w-md mx-auto">
          <div className="bg-[#FAF8F5] rounded-2xl p-8 border border-[#E8E4DF] scroll-reveal opacity-0" style={{ animationDelay: '0.2s' }}>
            <div className="w-14 h-14 flex items-center justify-center mb-6 bg-white rounded-full">
              <i className="ri-hand-heart-line text-3xl text-[#A0826D]"></i>
            </div>
            <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">PT는 선택입니다</h3>
            <p className="text-base text-[#6B6B6B] leading-relaxed">
              필요할 때만 안내합니다
            </p>
          </div>

          <div className="bg-[#FAF8F5] rounded-2xl p-8 border border-[#E8E4DF] scroll-reveal opacity-0" style={{ animationDelay: '0.4s' }}>
            <div className="w-14 h-14 flex items-center justify-center mb-6 bg-white rounded-full">
              <i className="ri-user-smile-line text-3xl text-[#A0826D]"></i>
            </div>
            <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">혼자서도 운동할 수 있도록</h3>
            <p className="text-base text-[#6B6B6B] leading-relaxed">
              최고의 환경을 제공해 드립니다
            </p>
          </div>

          <div className="bg-[#FAF8F5] rounded-2xl p-8 border border-[#E8E4DF] scroll-reveal opacity-0" style={{ animationDelay: '0.6s' }}>
            <div className="w-14 h-14 flex items-center justify-center mb-6 bg-white rounded-full">
              <i className="ri-sparkling-line text-3xl text-[#A0826D]"></i>
            </div>
            <h3 className="text-xl font-bold text-[#4A4A4A] mb-3">운동 후에도 불편함 ZERO</h3>
            <p className="text-base text-[#6B6B6B] leading-relaxed">
              탈의실, 샤워실까지 깔끔하게
            </p>
          </div>
        </div>
      </section>

      {/* 환경 & 이유 카드 섹션 */}
      <section className="py-16 px-6">
        <h2 className="text-2xl font-bold text-center mb-12 text-[#4A4A4A] scroll-reveal opacity-0" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
          편하게 다닐 수 있는 이유
        </h2>
        
        <div className="space-y-4 max-w-md mx-auto">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E8E4DF] flex items-center gap-4 scroll-reveal opacity-0" style={{ animationDelay: '0.2s' }}>
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-2xl text-[#A0826D]"></i>
            </div>
            <p className="text-base text-[#4A4A4A] font-medium">24시간 · 365일 연중무휴</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E8E4DF] flex items-center gap-4 scroll-reveal opacity-0" style={{ animationDelay: '0.3s' }}>
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-body-scan-line text-2xl text-[#A0826D]"></i>
            </div>
            <p className="text-base text-[#4A4A4A] font-medium">인바디 측정 가능</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E8E4DF] flex items-center gap-4 scroll-reveal opacity-0" style={{ animationDelay: '0.4s' }}>
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-medal-line text-2xl text-[#A0826D]"></i>
            </div>
            <p className="text-base text-[#4A4A4A] font-medium">프리미엄 운동기구</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E8E4DF] flex items-center gap-4 scroll-reveal opacity-0" style={{ animationDelay: '0.5s' }}>
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-line text-2xl text-[#A0826D]"></i>
            </div>
            <p className="text-base text-[#4A4A4A] font-medium">강요 없는 PT</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E8E4DF] flex items-center gap-4 scroll-reveal opacity-0" style={{ animationDelay: '0.6s' }}>
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-line text-2xl text-[#A0826D]"></i>
            </div>
            <p className="text-base text-[#4A4A4A] font-medium">매일 관리되는 시설</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E8E4DF] flex items-center gap-4 scroll-reveal opacity-0" style={{ animationDelay: '0.7s' }}>
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-heart-line text-2xl text-[#A0826D]"></i>
            </div>
            <p className="text-base text-[#4A4A4A] font-medium">여성 혼자도 편안한 분위기</p>
          </div>
        </div>
      </section>

      {/* 후기 스토리 섹션 */}
      <section className="py-16 px-6 bg-white">
        <h2 className="text-2xl font-bold text-center mb-12 text-[#4A4A4A] scroll-reveal opacity-0" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
          회원님들이 증명합니다
        </h2>
        
        <div className="space-y-5 max-w-md mx-auto">
          <div className="bg-[#FAF8F5] rounded-2xl p-8 border border-[#E8E4DF] scroll-reveal opacity-0" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#A0826D]/10 rounded-full flex-shrink-0">
                <i className="ri-seedling-line text-2xl text-[#A0826D]"></i>
              </div>
              <h3 className="text-lg font-bold text-[#4A4A4A]">처음 운동을 시작한 회원</h3>
            </div>
            <p className="text-base text-[#6B6B6B] leading-relaxed pl-15">
              "운동을 한 번도 안 해봤는데, 여기는 부담 없이 시작할 수 있었어요. 기구 사용법도 친절하게 알려주시고, 혼자 있어도 전혀 어색하지 않아요."
            </p>
          </div>

          <div className="bg-[#FAF8F5] rounded-2xl p-8 border border-[#E8E4DF] scroll-reveal opacity-0" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#A0826D]/10 rounded-full flex-shrink-0">
                <i className="ri-star-smile-line text-2xl text-[#A0826D]"></i>
              </div>
              <h3 className="text-lg font-bold text-[#4A4A4A]">혼자 운동이 가능해진 회원</h3>
            </div>
            <p className="text-base text-[#6B6B6B] leading-relaxed pl-15">
              "이제는 제 루틴대로 편하게 운동해요. PT 권유도 없고, 제 페이스대로 할 수 있어서 오히려 더 꾸준히 다니게 되더라고요."
            </p>
          </div>

          <div className="bg-[#FAF8F5] rounded-2xl p-8 border border-[#E8E4DF] scroll-reveal opacity-0" style={{ animationDelay: '0.6s' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#A0826D]/10 rounded-full flex-shrink-0">
                <i className="ri-calendar-check-line text-2xl text-[#A0826D]"></i>
              </div>
              <h3 className="text-lg font-bold text-[#4A4A4A]">장기 재등록 회원</h3>
            </div>
            <p className="text-base text-[#6B6B6B] leading-relaxed pl-15">
              "여러 헬스장을 다녀봤는데, 여기가 제일 오래 다니고 있어요. 시설도 깨끗하고, 24시간이라 새벽에도 운동할 수 있어서 좋아요."
            </p>
          </div>
        </div>
      </section>

      {/* 감정 정리 문장 */}
      <section className="py-20 px-6">
        <div className="max-w-md mx-auto text-center">
          <p className="text-2xl font-bold text-[#4A4A4A] leading-relaxed scroll-reveal opacity-0" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
            헬스장 유목민들의 정착지
          </p>
        </div>
      </section>

      {/* 시설 요약 섹션 */}
      <section className="py-16 px-6 bg-white">
        <h2 className="text-2xl font-bold text-center mb-12 text-[#4A4A4A] scroll-reveal opacity-0" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
          올드짐 수월점
        </h2>
        
        <div className="space-y-10 max-w-md mx-auto">
          {/* 사진 1: 유산소 & 전반 전경 */}
          <div className="scroll-reveal opacity-0" style={{ animationDelay: '0.2s' }}>
            <div className="w-full h-64 mb-4 rounded-xl overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/44ad2e8efa092dafc98e4f954e0bc538/b6c0d0a2ad4ec32a9860777cd6f852ca.png"
                alt="유산소 기구 존"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="text-center space-y-1">
              <p className="text-base text-[#4A4A4A]">유산소 기구 완비</p>
              <p className="text-base text-[#4A4A4A]">천국의 계단 포함</p>
            </div>
          </div>

          {/* 사진 2: 웨이트 & 머신 존 */}
          <div className="scroll-reveal opacity-0" style={{ animationDelay: '0.4s' }}>
            <div className="w-full h-64 mb-4 rounded-xl overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/44ad2e8efa092dafc98e4f954e0bc538/afbc0080dda5a7436e626ec365d77bf2.png"
                alt="웨이트 머신 존"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="text-center space-y-1">
              <p className="text-base text-[#4A4A4A]">초급자부터 상급자까지</p>
              <p className="text-base text-[#4A4A4A]">다양한 머신 구성</p>
            </div>
          </div>
        </div>
      </section>

      {/* 최종 CTA */}
      <section className="py-16 px-6 pb-24 bg-white">
        <div className="max-w-md mx-auto text-center">
          <a
            href="https://naver.me/FAR5tVnT"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-[#4A4A4A] text-white text-lg font-bold py-5 rounded-full shadow-lg hover:bg-[#3A3A3A] transition-colors whitespace-nowrap cursor-pointer scroll-reveal opacity-0"
          >
            상담 한 번 받아볼게요
          </a>
          <p className="mt-4 text-sm text-[#8B8B8B] scroll-reveal opacity-0" style={{ animationDelay: '0.3s' }}>
            등록 권유 없이, 편하게 안내만 드립니다
          </p>
        </div>
      </section>

      {/* 상담 신청 폼 팝업 */}
      {showConsultForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full relative">
            <button
              onClick={() => setShowConsultForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            
            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-[#A0826D]/10 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-[#A0826D]"></i>
                </div>
                <p className="text-xl font-bold text-[#4A4A4A] mb-2">신청이 완료되었습니다</p>
                <p className="text-sm text-[#6B6B6B]">빠른 시일 내에 연락드리겠습니다</p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-[#4A4A4A] mb-6 text-center">상담 신청</h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="oldgym-consult">
                  <div>
                    <label className="block text-sm font-medium text-[#4A4A4A] mb-2">이름</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full px-4 py-3 border border-[#E8E4DF] rounded-lg text-sm focus:outline-none focus:border-[#A0826D]"
                      placeholder="이름을 입력해주세요"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#4A4A4A] mb-2">연락처</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="w-full px-4 py-3 border border-[#E8E4DF] rounded-lg text-sm focus:outline-none focus:border-[#A0826D]"
                      placeholder="010-0000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#4A4A4A] mb-2">문의사항 (선택)</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({...formData, message: e.target.value});
                        }
                      }}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-[#E8E4DF] rounded-lg text-sm focus:outline-none focus:border-[#A0826D] resize-none"
                      rows={4}
                      placeholder="궁금하신 점을 자유롭게 작성해주세요 (최대 500자)"
                    />
                    <p className="text-xs text-[#8B8B8B] mt-1 text-right">{formData.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#4A4A4A] text-white text-base font-bold py-4 rounded-full hover:bg-[#3A3A3A] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? '전송 중...' : '상담 신청하기'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
